import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Snackbar,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { setChatDialog, openTaskChatDialog, closeChatNotice } from './redux';
import { withChatBoundary } from './ChatErrorBoundary';
import { senderType, chatUnreadCount } from './chatState';

const roleLabel = { [senderType.technician]: 'техник', [senderType.coordinator]: 'координатор' };
const previewLength = 120;

const preview = notice => {
  const text = (notice.text || '').trim();
  if (!text) return notice.hasAttachments ? 'Снимка' : '';
  return text.length > previewLength ? `${text.slice(0, previewLength)}…` : text;
};

// Известията в таблета (функционалното описание, т. 4.2.1 и 4.2.2): ново съобщение от координатор или колега — отгоре,
// с броя непрочетени в чата, докато не се види или затвори; смяна на темата от координатора — прозорец „от … на …",
// и когато чатът е отворен. „Отвори" води в чата на задачата / в общия чат.
const ChatNotices = () => {
  const dispatch = useDispatch();
  const messageNotice = useSelector(state => state.chat.messageNotice);
  const topicNotice = useSelector(state => state.chat.topicNotice);
  const viewingChatId = useSelector(state => state.chat.viewingChatId);
  const tasks = useSelector(state => state.tasks.data.tasks);
  const unread = useSelector(state =>
    messageNotice ? chatUnreadCount(state.chat, x => x.id === messageNotice.chatId) : 0
  );

  const taskOf = notice =>
    notice.scheduleTaskId && tasks.find(x => Number(x.id) === notice.scheduleTaskId);
  const placeOf = notice => {
    if (!notice.scheduleTaskId) return 'Общ чат';
    const task = taskOf(notice);
    return (task && [task.client, task.address].filter(Boolean).join(', ')) || 'Чат по задача';
  };

  const open = (notice, kind) => {
    dispatch(closeChatNotice(kind));
    const task = taskOf(notice);
    if (task) dispatch(openTaskChatDialog(task));
    else
      dispatch(
        setChatDialog({ open: true, scheduleTaskId: notice.scheduleTaskId || null, title: '' })
      );
  };

  const sender = messageNotice
    ? [messageNotice.senderName, roleLabel[messageNotice.senderType]].filter(Boolean).join(' · ')
    : '';

  return (
    <>
      {/* Ново съобщение, докато предишното още се вижда — отначало, с пълното време. */}
      <Snackbar
        key={messageNotice ? messageNotice.messageId : 0}
        open={!!messageNotice}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={15000}
        onClose={(event, reason) => reason !== 'clickaway' && dispatch(closeChatNotice('message'))}
        message={
          messageNotice && (
            <div>
              <Typography style={{ fontWeight: 'bold' }}>
                {`Ново съобщение${unread > 1 ? ` (непрочетени: ${unread})` : ''} — ${placeOf(
                  messageNotice
                )}`}
              </Typography>
              <Typography>{[sender, preview(messageNotice)].filter(Boolean).join(': ')}</Typography>
            </div>
          )
        }
        action={
          messageNotice && (
            <>
              <Button color="secondary" onClick={() => open(messageNotice, 'message')}>
                Отвори
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => dispatch(closeChatNotice('message'))}
              >
                <Close fontSize="small" />
              </IconButton>
            </>
          )
        }
      />

      <Dialog open={!!topicNotice} onClose={() => dispatch(closeChatNotice('topic'))}>
        {topicNotice && (
          <>
            <DialogTitle>Темата е сменена</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>{placeOf(topicNotice)}</Typography>
              <Typography>{topicNotice.text}</Typography>
            </DialogContent>
            <DialogActions>
              {topicNotice.chatId !== viewingChatId && (
                <Button color="secondary" onClick={() => open(topicNotice, 'topic')}>
                  Отвори чата
                </Button>
              )}
              <Button variant="contained" onClick={() => dispatch(closeChatNotice('topic'))}>
                Добре
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default withChatBoundary(ChatNotices);
